import { cn } from '@worldcoo/ui/lib/utils';
import { MenuIcon, XIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@worldcoo/ui/components/drawer';
import { FacebookIcon, InstagramIcon, XSocialIcon } from './icons';
import { LogoIcon } from './logo-icon';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { STYLE_WITH_PRIMARY_COLOR } from './utils';

export function Header() {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	const languages = [
		{ code: 'es', label: 'ESP' },
		{ code: 'en', label: 'ENG' },
		{ code: 'ca', label: 'CAT' },
		{ code: 'it', label: 'IT' },
	] as const;

	return (
		<>
			{/* Background so the backdrop merges with the hero color when top: 0 */}
			<div className="bg-hero absolute -z-10 h-[100px] w-full" />
			<header className="bg-hero/50 sticky top-0 z-10 backdrop-blur-md">
				<div className="mx-auto flex max-w-[1310px] items-center py-4 font-medium">
					{/* Logo */}
					<Link to="/www" activeOptions={{ exact: true }} className="pr-6 pl-4">
						<span className="sr-only">{t`Start`}</span>
						<LogoIcon
							className="h-[67.36px] w-[120px]"
							style={{ '--color-primary': 'var(--primary)' } as React.CSSProperties}
						/>
					</Link>
					{/* Navigation - Desktop */}
					<nav className="mx-auto hidden px-2 xl:flex">
						<Link
							to="/www/round-up"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`Charity Rounding Up`}
						</Link>
						<a href="https://cert.worldcoo.com/" className="hover:text-primary truncate p-4 transition-colors">
							{t`Donation Certificate`}
						</a>
						<Link
							to="/www/about-us"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`About Us`}
						</Link>
						<Link
							to="/www/transparency"
							className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
						>
							{t`Transparency`}
						</Link>
					</nav>
					{/* Language Selector and Contact - Desktop */}
					<nav className="mr-6 hidden items-center xl:flex">
						<Link
							to="/www/contact"
							className="hover:text-primary aria-[current=page]:text-primary mr-10 p-4 transition-colors"
						>
							{t`Contact`}
						</Link>
						{languages.map((lang) => (
							<button
								key={lang.code}
								onClick={() => changeLanguage(lang.code)}
								className={cn(
									'hover:text-primary cursor-pointer rounded-full px-2.5 py-1 text-sm font-bold transition-colors',
									language === lang.code && 'border-foreground border',
								)}
							>
								{lang.label}
							</button>
						))}
					</nav>

					{/* Mobile Menu */}
					<Drawer direction="right">
						<DrawerTrigger className="ml-auto xl:hidden">
							<MenuIcon className="mx-8 size-8" />
						</DrawerTrigger>
						<DrawerContent className="bg-hero" aria-describedby={undefined}>
							<DrawerHeader>
								<DrawerClose className="m-4">
									<span className="sr-only">{t`Close`}</span>
									<XIcon />
								</DrawerClose>
								<div className="sr-only">
									<DrawerTitle>{t`Navigation menu`}</DrawerTitle>
								</div>
							</DrawerHeader>
							{/* Mobile Language Selector */}
							<nav className="mx-auto ml-auto items-center">
								{languages.map((lang) => (
									<button
										key={lang.code}
										onClick={() => changeLanguage(lang.code)}
										className={cn(
											'hover:text-primary rounded-full px-2.5 py-1 text-sm font-bold transition-colors',
											language === lang.code && 'border-foreground border',
										)}
									>
										{lang.label}
									</button>
								))}
							</nav>
							{/* Mobile Navigation */}
							<nav className="flex flex-col p-4 text-end text-xl font-semibold" style={STYLE_WITH_PRIMARY_COLOR}>
								<Link
									to="/www"
									activeOptions={{ exact: true }}
									className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
								>
									{t`Start`}
								</Link>
								<Link
									to="/www/round-up"
									className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
								>
									{t`Charity Rounding Up`}
								</Link>
								<a href="https://cert.worldcoo.com/" className="hover:text-primary truncate p-4 transition-colors">
									{t`Donation Certificate`}
								</a>
								<Link
									to="/www/about-us"
									className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
								>
									{t`About Us`}
								</Link>
								<Link
									to="/www/transparency"
									className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
								>
									{t`Transparency`}
								</Link>
								<Link
									to="/www/contact"
									className="hover:text-primary aria-[current=page]:text-primary truncate p-4 transition-colors"
								>
									{t`Contact`}
								</Link>
							</nav>
							{/* Mobile Social Links */}
							<div className="mx-8 mt-auto mb-40 flex flex-col gap-6 text-end">
								<p className="text-lg font-semibold">{t`Join our community`}</p>
								<ul className="flex justify-end gap-6">
									<li>
										<a href="https://www.instagram.com/worldcoo/" target="_blank" rel="noopener noreferrer">
											<span className="sr-only">Instagram</span>
											<div className="bg-footer fill-hero hover:bg-primary size-7 rounded-full p-1.5 transition-colors">
												<InstagramIcon />
											</div>
										</a>
									</li>
									<li>
										<a href="https://twitter.com/worldcoo" target="_blank" rel="noopener noreferrer">
											<span className="sr-only">Twitter</span>
											<div className="bg-footer fill-hero hover:bg-primary size-7 rounded-full p-1.5 transition-colors">
												<XSocialIcon />
											</div>
										</a>
									</li>
									<li>
										<a href="https://www.facebook.com/worldcoo/" target="_blank" rel="noopener noreferrer">
											<span className="sr-only">Facebook</span>
											<div className="fill-footer hover:fill-primary size-7 rounded-full transition-colors">
												<FacebookIcon />
											</div>
										</a>
									</li>
									<li>
										<a href="https://www.linkedin.com/company/worldcoo" target="_blank" rel="noopener noreferrer">
											<span className="sr-only">LinkedIn</span>
											<div className="bg-footer text-hero hover:bg-primary size-7 rounded-full p-1.5 text-center text-lg leading-none font-bold transition-colors">
												in
											</div>
										</a>
									</li>
								</ul>
							</div>
						</DrawerContent>
					</Drawer>
				</div>
			</header>
		</>
	);
}
